import { IkeFieldType, IkeToken } from '@ikegps/core-api';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { ProFeatureTooltip } from '../../components/pro.feature.tooltip';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Services } from '../../services';
import { classNames } from '../../util/classnames';
import { CSS, Style } from './form.palette.style';

addStyles(Style);

export interface FormPaletteOption {
	icon: MaterialIcon;
	title: T;
	maxDepth?: number;
	/** Token gating this field type. Absent = always offered. A single token suffices: the IKE Analyze
	 *  group grants the individual feature tokens server-side (see permission.ts). */
	token?: IkeToken;
}

export const FIELD_TYPE_DISPLAY: { [k in IkeFieldType]: FormPaletteOption } = {
	[IkeFieldType.TEXT]: { icon: MaterialIcon.FIELD_TEXT, title: T.FIELD_TEXT },
	[IkeFieldType.HEIGHT]: { icon: MaterialIcon.FIELD_HEIGHT, title: T.FIELD_HEIGHT },
	[IkeFieldType.NUMBER]: { icon: MaterialIcon.FIELD_NUMBER, title: T.FIELD_NUMBER },
	[IkeFieldType.ORIENTATION]: { icon: MaterialIcon.FIELD_ORIENTATION, title: T.FIELD_ORIENTATION },
	[IkeFieldType.TRUE_SIZE]: { icon: MaterialIcon.FIELD_TRUE_SIZE, title: T.FIELD_TRUE_SIZE },
	[IkeFieldType.DATE]: { icon: MaterialIcon.DATE, title: T.FIELD_DATE },
	[IkeFieldType.SELECT_LIST]: { icon: MaterialIcon.FIELD_SELECT_LIST, title: T.FIELD_SELECT_LIST },
	[IkeFieldType.YES_NO]: { icon: MaterialIcon.CHECK_BOX_SELECTED, title: T.FIELD_YES_NO },
	[IkeFieldType.NESTED_LIST]: { icon: MaterialIcon.FIELD_NESTED_LIST, title: T.FIELD_NESTED_LIST },
	[IkeFieldType.FORM]: { icon: MaterialIcon.FIELD_FORM, title: T.FIELD_FORM, maxDepth: 3 },
	[IkeFieldType.LOCATION]: { icon: MaterialIcon.FIELD_LOCATION, title: T.FIELD_LOCATION },
	[IkeFieldType.IMAGE]: { icon: MaterialIcon.FIELD_IMAGE, title: T.FIELD_IMAGE },
	[IkeFieldType.VECTOR]: { icon: MaterialIcon.FIELD_VECTOR, title: T.FIELD_VECTOR },
	[IkeFieldType.ANCHOR]: { icon: MaterialIcon.FIELD_ANCHOR, title: T.FIELD_ANCHOR },
	[IkeFieldType.VERTICAL_SEPARATION]: {
		icon: MaterialIcon.VERTICAL_SEPARATION,
		title: T.FIELD_VERTICAL_SEPARATION,
		token: IkeToken.FEATURE_FORM_VERTICAL_SEPARATION,
	},
	[IkeFieldType.FILE_TOOL]: {
		icon: MaterialIcon.CLOUD_UPLOAD,
		title: T.FIELD_FILE_TOOL,
		token: IkeToken.FEATURE_GROUP_IKE_ANALYZE,
	},
	[IkeFieldType.LINE]: { icon: MaterialIcon.FIELD_LINE, title: T.FIELD_LINE, token: IkeToken.FEATURE_LINE_TOOL },
};

export function isFieldTypeAllowed(fieldType: IkeFieldType): boolean {
	const { token } = FIELD_TYPE_DISPLAY[fieldType];
	return token == null || Services.Token.has(token);
}

export const FormDesignerPalette = {
	viewPaletteIcon(fieldType: IkeFieldType, allowed: boolean, onclick: (fieldType: IkeFieldType) => void): MithrilVNode {
		const tile = m(
			'div',
			{
				className: classNames(`${CSS.field.container} ${CSS.field.container}--${fieldType}`, {
					[CSS.field.disabled]: !allowed,
				}),
				onclick: allowed
					? () => {
							onclick(fieldType);
							Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:palette:add', fieldType);
						}
					: undefined,
			},
			[this.viewIcon(fieldType), this.viewTitle(fieldType)]
		);

		// Gated field types stay in the palette but inert, with a hover tooltip explaining why.
		return allowed ? tile : ProFeatureTooltip.view({ children: tile });
	},

	viewInline(fieldType: IkeFieldType): MithrilVNode {
		return m.fragment({}, [this.viewIcon(fieldType), this.viewTitle(fieldType)]);
	},

	viewIcon(fieldType: IkeFieldType, opts?): MithrilVNode {
		return Icon.view(FIELD_TYPE_DISPLAY[fieldType].icon, opts);
	},

	viewTitle(fieldType: IkeFieldType): MithrilVNode {
		return m('div', { className: CSS.field.title }, FormDesignerPalette.translate(fieldType));
	},

	translate(fieldType: IkeFieldType): string {
		return Translate(FIELD_TYPE_DISPLAY[fieldType].title);
	},

	view(onclick: (fieldType: IkeFieldType) => void, depth = 0): MithrilVNode {
		const children: MithrilVNode[] = (Object.keys(FIELD_TYPE_DISPLAY) as (keyof typeof FIELD_TYPE_DISPLAY)[])
			// maxDepth is a structural nesting limit, not a permission — keep hiding those outright.
			.filter((key) => {
				const option = FIELD_TYPE_DISPLAY[key];

				return !(option.maxDepth != null && option.maxDepth < depth);
			})
			.map((key) => FormDesignerPalette.viewPaletteIcon(key, isFieldTypeAllowed(key), onclick));

		return m('div', { className: CSS.container }, [
			m('div', { className: CSS.title }, Translate(T.FORM_FIELD_CHOOSE)),
			m('div', { className: CSS.list.container }, children),
		]);
	},
};
